/**
 * FormFields.tsx
 *
 * Re-exports form field components from the design system with app-specific
 * date utilities configured. Use these components throughout the frontend
 * for consistent form styling.
 *
 * Components available:
 * - Label (FormLabel) - Form label with optional tooltip and required indicator
 * - Field (TextField) - Text input with label, error, copy functionality
 * - DateField - Date picker with label, error, copy functionality (`fieldAnchor` for scroll-to-field)
 * - BooleanField - Yes/No/N/A dropdown
 * - DropdownField - Virtualized combobox for large option lists
 *
 * Size variants: 'sm' | 'default' | 'lg'
 * - sm: h-7, text-xs (compact forms)
 * - default: h-8, text-sm (standard forms) - DEFAULT
 * - lg: h-9, text-sm (larger touch targets)
 */

import React from 'react';
import {
  FormLabel,
  TextField as DesignSystemTextField,
  DateField as DesignSystemDateField,
  BooleanField as DesignSystemBooleanField,
  DropdownField as DesignSystemDropdownField,
} from '@roger/ui-components';
import type {
  TextFieldProps as DesignSystemTextFieldProps,
  DateFieldProps as DesignSystemDateFieldProps,
  BooleanFieldProps as DesignSystemBooleanFieldProps,
  DropdownFieldProps as DesignSystemDropdownFieldProps,
} from '@roger/ui-components';
import {
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../utils/dateUtils';

// ============================================================================
// Label Component
// ============================================================================

// Re-export FormLabel as Label for backwards compatibility
export const Label = FormLabel;

// ============================================================================
// Field Component (TextField wrapper with cursor preservation)
// ============================================================================

type FieldProps = Omit<DesignSystemTextFieldProps, 'fieldSize'> & {
  fieldSize?: 'sm' | 'default' | 'lg';
  // Enable cursor position preservation for formatted inputs (SSN, phone, zip)
  maintainCursorPosition?: boolean;
};

export const Field = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  FieldProps
>(
  (
    {
      fieldSize = 'default',
      maintainCursorPosition = false, // This is used to maintain cursor position when we run auto-formatting on the fly as user types
      onChange,
      inputProps,
      ...props
    },
    ref
  ) => {
    const localRef = React.useRef<
      HTMLInputElement | HTMLTextAreaElement | null
    >(null);
    const cursorPositionRef = React.useRef<number | null>(null);
    const unformattedValueRef = React.useRef<string | null>(null);

    // Get the value to track - either from controlled props or inputProps
    const trackedValue = props.value ?? inputProps?.value;

    // Merge refs to keep local access while forwarding
    const mergedRef = React.useCallback(
      (element: HTMLInputElement | HTMLTextAreaElement | null) => {
        localRef.current = element;
        if (typeof ref === 'function') {
          ref(element);
        } else if (ref) {
          // Object refs need their current property set
          const mutableRef = ref as {
            current: HTMLInputElement | HTMLTextAreaElement | null;
          };
          mutableRef.current = element;
        }
      },
      [ref]
    );

    // Calculate new cursor position after formatting.
    // Counts alphanumeric characters before the cursor and finds the equivalent
    // position in the formatted value. Alphanumerics (not just digits) are the
    // stable anchor because formatters only insert/strip separators (dashes,
    // spaces) or change case — they preserve the alphanumeric characters the
    // user typed. This keeps the caret correct for letter-bearing formats (MBI)
    // as well as the all-numeric ones (SSN, phone, zip).
    const ALNUM = /[a-zA-Z0-9]/;
    const calculateNewCursorPosition = (
      unformatted: string,
      formatted: string,
      cursorPos: number
    ): number => {
      const alnumBeforeCursor = (
        unformatted.slice(0, cursorPos).match(/[a-zA-Z0-9]/g) || []
      ).length;
      if (alnumBeforeCursor === 0) return 0;

      let alnumCount = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (ALNUM.test(formatted[i])) alnumCount++;
        if (alnumCount === alnumBeforeCursor) return i + 1;
      }
      return formatted.length;
    };

    // Restore cursor position after value changes from formatting
    React.useLayoutEffect(() => {
      if (
        maintainCursorPosition &&
        cursorPositionRef.current !== null &&
        unformattedValueRef.current !== null &&
        localRef.current &&
        'setSelectionRange' in localRef.current &&
        unformattedValueRef.current !== trackedValue
      ) {
        const newPos = calculateNewCursorPosition(
          unformattedValueRef.current,
          String(trackedValue || ''),
          cursorPositionRef.current
        );
        localRef.current.setSelectionRange(newPos, newPos);
      }
      cursorPositionRef.current = null;
      unformattedValueRef.current = null;
    }, [trackedValue, maintainCursorPosition]);

    // Wrap onChange to capture cursor position before formatting
    const handleChange = React.useCallback(
      (value: string) => {
        if (maintainCursorPosition && localRef.current) {
          cursorPositionRef.current = (
            localRef.current as HTMLInputElement
          ).selectionStart;
          unformattedValueRef.current = value;
        }
        onChange?.(value);
      },
      [onChange, maintainCursorPosition]
    );

    // Wrap inputProps.onChange to capture cursor position before formatting
    const wrappedInputProps = React.useMemo(() => {
      if (!inputProps || !maintainCursorPosition || !inputProps.onChange) {
        return inputProps;
      }

      const originalOnChange = inputProps.onChange;
      return {
        ...inputProps,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          if (localRef.current) {
            cursorPositionRef.current = (
              localRef.current as HTMLInputElement
            ).selectionStart;
            unformattedValueRef.current = e.target.value;
          }
          originalOnChange(e);
        },
      };
    }, [inputProps, maintainCursorPosition]);

    return (
      <DesignSystemTextField
        ref={mergedRef}
        fieldSize={fieldSize}
        onChange={onChange ? handleChange : undefined}
        inputProps={wrappedInputProps}
        {...props}
      />
    );
  }
);

Field.displayName = 'Field';

// ============================================================================
// DateField Component (with app date utilities)
// ============================================================================

type DateFieldProps = Omit<
  DesignSystemDateFieldProps,
  'formatDateToISO' | 'formatDateToDisplay' | 'parseISOToDate' | 'fieldSize'
> & {
  fieldSize?: 'sm' | 'default' | 'lg';
};

export const DateField: React.FC<DateFieldProps> = ({
  fieldSize = 'default',
  ...props
}) => {
  return (
    <DesignSystemDateField
      {...props}
      fieldSize={fieldSize}
      formatDateToISO={formatDateToISO}
      formatDateToDisplay={formatDateToMMDDYYYY}
      parseISOToDate={parseISOToLocalDate}
    />
  );
};

// ============================================================================
// BooleanField Component (with 'default' size)
// ============================================================================

type BooleanFieldProps = Omit<DesignSystemBooleanFieldProps, 'fieldSize'> & {
  fieldSize?: 'sm' | 'default' | 'lg';
};

export const BooleanField: React.FC<BooleanFieldProps> = ({
  fieldSize = 'default',
  ...props
}) => {
  return <DesignSystemBooleanField fieldSize={fieldSize} {...props} />;
};

// ============================================================================
// DropdownField Component (with 'default' size)
// ============================================================================

type DropdownFieldProps = Omit<DesignSystemDropdownFieldProps, 'fieldSize'> & {
  fieldSize?: 'sm' | 'default' | 'lg';
};

export const DropdownField: React.FC<DropdownFieldProps> = ({
  fieldSize = 'default',
  ...props
}) => {
  return <DesignSystemDropdownField fieldSize={fieldSize} {...props} />;
};
